import intl from 'react-intl-universal';
import { useState, useEffect, useRef } from 'react';
import { BetaSchemaForm } from '@ant-design/pro-components';
import type {
    ProFormColumnsType,
    ProFormProps,
    ProFormInstance,
} from '@ant-design/pro-components';
import { Row, Col, message } from 'antd';
import { default as Button } from '@/components/CssBtn';

import { getTHAddress, getCommonAddress } from '@/api/addr';
import { isArray, isNumber } from 'lodash';
import { getTextByEncryp, getCommonEnumInfo } from '@/api/common';
// import sensorLoader from '@/utils/sensorLoader'

interface Props extends ProFormProps {
    type: number; // 1: 收件人； 2: 取件人
    id: string; // 主键id
    showGetOriginBtn?: boolean; // 是否展示获取原订单地址信息按钮
    address_info?: any; // 原订单地址信息
    readonly: boolean;
    value: any;
    onChange: (values: any) => void;
    // 获取原订单地址加密信息字段
    position?: number;
    sourceNo?: string;
    countryCode?: string;
    express_company_options?: any[];
}

const Addrs: React.FC<Props> = (props) => {
    const {
        type = 1,
        id,
        value,
        labelCol,
        onChange,
        address_info,
        readonly = false,
        showGetOriginBtn = true,
        position,
        sourceNo,
        countryCode,
        express_company_options,
    } = props;

    // const currentValue = useRef(value);
    const country = countryCode?.toLocaleUpperCase();
    const [isTH] = useState<boolean>(country === 'TH');
    const [isRequiredCard] = useState<boolean>(['TW'].includes(country));
    // 'BR', 'ID', 'KR'
    const addrForm = useRef<ProFormInstance>(null);

    // 省市区数据
    const [provinces, setProvinces] = useState<string[]>([]);
    const [cities, setCities] = useState<{ [key: string]: string[] } | null>(
        null,
    );
    const [districts, setDistricts] = useState<{
        [key: string]: string[];
    } | null>([]);
    // const cacheValue = useRef(value);

    useEffect(() => {
        if (isTH) {
            getTHAddress().then((res) => {
                setProvinces(res?.provinces || []);
                setCities(res?.cities || null);
                setDistricts(res?.post_codes || null);
                console.log(res);
            });
        } else {
            getCommonAddress().then((res) => {
                console.log(
                    res,
                    countryCode?.toLocaleLowerCase(),
                    res?.provinceList?.[countryCode?.toLocaleLowerCase()]?.map(
                        (item) => item.enName,
                    ),
                );
                setProvinces(
                    res?.provinceList?.[countryCode?.toLocaleLowerCase()]?.map(
                        (item) => item.enName,
                    ),
                );
            });
        }
    }, [isTH]);

    useEffect(() => {
        if (readonly && addrForm.current) {
            addrForm.current?.setFieldsValue({
                [id]: value,
            });
        }
    }, [readonly, value]);

    const handleProvinceChange = (
        provinceName: string,
        a: any,
        isSaveCity: boolean = false,
    ) => {
        if (isTH) {
            const currentProvince = provinces.find(
                (province) =>
                    province === provinceName ||
                    // 这里是容错，value是【北京市】 但是值是【北京】的情况
                    province.includes(provinceName) ||
                    provinceName?.includes(province),
            );

            if (currentProvince) {
                if (!isSaveCity) {
                    // 如果是回显 比如获取原地址信息，则不 重置 市区的值
                    onItemChange({
                        province: provinceName,
                        city: cities[0]?.name,
                        zip_code: districts[0]?.name,
                    });
                }
            }
        }
    };

    const handleCityChange = (cityName: string) => {
        let zip_code = districts?.[`${value.province}_${cityName}`];
        onItemChange({
            city: cityName,
            zip_code: isArray(zip_code) ? zip_code[0] : zip_code,
        });
    };

    const onItemChange = (itemValue: any) => {
        onChange({
            ...(value || {}),
            country: countryCode?.toLocaleUpperCase(),
            ...itemValue,
        });
    };

    const getAddrColumns = (): ProFormColumnsType<any, 'addrSelectDate'>[] => {
        const namePreFix = type === 1 ? intl.get('收件') : intl.get('取件');
        const items: ProFormColumnsType<any, 'addrSelectDate'>[] = [
            {
                title: intl.get('{namePreFix}人', { namePreFix: namePreFix }),
                dataIndex: [id, 'name'],
                formItemProps: {
                    rules: [
                        {
                            required: true,
                            message: intl.get('请输入{namePreFix}人', {
                                namePreFix: namePreFix,
                            }),
                        },
                    ],

                    labelCol: labelCol || { span: 3 },
                },
                fieldProps: {
                    maxLength: 20,
                    onChange: (e) => {
                        onItemChange({
                            name: e.target.value,
                        });
                    },
                },
            },
            {
                title: intl.get('联系电话'),
                dataIndex: [id, 'phone'],
                formItemProps: {
                    rules: [
                        {
                            required: true,
                            message: intl.get(intl.get('请输入联系电话')),
                        },
                        // ...commonRules.mobile,
                    ],
                    labelCol: labelCol || { span: 3 },
                },
                fieldProps: {
                    // maxLength: commonRules.mobileLength,
                    onChange: (e) => {
                        onItemChange({
                            phone: e.target.value,
                        });
                    },
                },
            },
            !isTH && {
                valueType: 'group',
                columns: [
                    {
                        title: intl.get('{namePreFix}地址', {
                            namePreFix: namePreFix,
                        }),
                        dataIndex: [id, 'province'],
                        valueType: provinces?.length ? 'select' : 'text',
                        className: 'group-item',
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        '请输入{namePreFix}地址',
                                        {
                                            namePreFix: namePreFix,
                                        },
                                    ),
                                },
                            ],
                            labelCol: labelCol || { span: 3 },
                        },
                        fieldProps: {
                            placeholder: intl.get('请选择省'),
                            options: provinces?.map((province) => ({
                                label: province,
                                value: province,
                            })),
                            onChange: handleProvinceChange,
                        },
                    },
                    {
                        dataIndex: [id, 'city'],
                        valueType: 'text',
                        className: 'group-item',
                        colProps: {
                            span: isNumber(labelCol?.span)
                                ? 24 - Number(labelCol?.span)
                                : 21,
                            offset: labelCol?.span || 3,
                        },
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        '请输入{namePreFix}地址',
                                        {
                                            namePreFix: namePreFix,
                                        },
                                    ),
                                },
                            ],

                            // labelCol: labelCol || { span: 3 },
                        },
                        fieldProps: {
                            placeholder: intl.get('请输入城市 / 区 / 镇 / 村'),
                        },
                    },
                    {
                        dataIndex: [id, 'detail_address'],
                        valueType: 'textarea',
                        colProps: {
                            span: isNumber(labelCol?.span)
                                ? 24 - Number(labelCol?.span)
                                : 21,
                            offset: labelCol?.span || 3,
                        },
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        intl.get('请输入门牌号等'),
                                    ),
                                },
                            ],
                        },
                        render: (text) => text,
                        fieldProps: {
                            placeholder: intl.get('请输入门牌号等'),
                            maxLength: 100,
                            // onBlur: detailAddressBlur,
                        },
                    },
                ],
            },
            isTH && {
                valueType: 'group',
                columns: [
                    {
                        title: intl.get('{namePreFix}地址', {
                            namePreFix: namePreFix,
                        }),
                        dataIndex: [id, 'province'],
                        valueType: 'select',
                        className: 'group-item',
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        '请输入{namePreFix}地址',
                                        {
                                            namePreFix: namePreFix,
                                        },
                                    ),
                                },
                            ],

                            labelCol: labelCol || { span: 3 },
                        },
                        fieldProps: {
                            placeholder: intl.get('请选择省'),
                            options: provinces?.map((province) => ({
                                label: province,
                                value: province,
                            })),
                            onChange: handleProvinceChange,
                        },
                    },
                    {
                        valueType: 'dependency',
                        name: [id, 'province'],
                        columns: (value) => {
                            console.log(value);
                            const province = value[id]?.province;
                            return [
                                {
                                    dataIndex: [id, 'city'],
                                    valueType: 'select',
                                    colProps: {
                                        span: isNumber(labelCol?.span)
                                            ? 24 - Number(labelCol?.span)
                                            : 21,
                                        offset: labelCol?.span || 3,
                                    },
                                    formItemProps: {
                                        rules: [
                                            {
                                                required: true,
                                                message: intl.get(
                                                    '请输入{namePreFix}地址',
                                                    {
                                                        namePreFix: namePreFix,
                                                    },
                                                ),
                                            },
                                        ],
                                    },
                                    fieldProps: {
                                        placeholder: intl.get('请选择市'),
                                        options: cities?.[province]?.map(
                                            (city) => ({
                                                label: city,
                                                value: city,
                                            }),
                                        ),
                                        onChange: handleCityChange,
                                    },
                                },
                            ];
                        },
                    },
                    {
                        valueType: 'dependency',
                        name: [id],
                        columns: (value) => {
                            const province = value[id]?.province;
                            const city = value[id]?.city;

                            return [
                                {
                                    dataIndex: [id, 'zip_code'],
                                    valueType: 'select',
                                    colProps: {
                                        span: isNumber(labelCol?.span)
                                            ? 24 - Number(labelCol?.span)
                                            : 21,
                                        offset: labelCol?.span || 3,
                                    },
                                    formItemProps: {
                                        rules: [
                                            {
                                                required: true,
                                                message: intl.get(
                                                    intl.get('请选择邮编'),
                                                ),
                                            },
                                        ],
                                    },
                                    fieldProps: {
                                        placeholder: intl.get('请选择邮编'),
                                        options: districts?.[
                                            `${province}_${city}`
                                        ]?.map((district) => ({
                                            label: district,
                                            value: district,
                                        })),
                                    },
                                },
                            ];
                        },
                    },

                    {
                        dataIndex: [id, 'detail_address'],
                        valueType: 'textarea',
                        colProps: {
                            span: isNumber(labelCol?.span)
                                ? 24 - Number(labelCol?.span)
                                : 21,
                            offset: labelCol?.span || 3,
                        },
                        formItemProps: {
                            rules: [
                                {
                                    required: true,
                                    message: intl.get(
                                        intl.get('请输入详细地址'),
                                    ),
                                },
                            ],
                        },
                        render: (text) => text,
                        fieldProps: {
                            placeholder: intl.get('请输入详细地址'),
                            maxLength: 100,
                            // onBlur: detailAddressBlur,
                        },
                    },
                ],
            },
            !isTH && {
                title: intl.get('邮编'),
                dataIndex: [id, 'zip_code'],
                formItemProps: {
                    rules: [
                        {
                            required: true,
                            message: intl.get(intl.get('请输入邮编')),
                        },
                        // ...commonRules.mobile,
                    ],
                    labelCol: labelCol || { span: 3 },
                },
                fieldProps: {
                    // maxLength: commonRules.mobileLength,
                    onChange: (e) => {
                        onItemChange({
                            zip_code: e.target.value,
                        });
                    },
                },
            },
            {
                valueType: 'select',
                dataIndex: [id, 'express_company'],
                title: intl.get('选择寄送快递'),
                fieldProps: {
                    options: express_company_options || [],
                },
                formItemProps: {
                    rules: [
                        {
                            required: true,
                            message: intl.get('请选择寄送快递'),
                        },
                    ],

                    labelCol: labelCol || { span: 3 },
                },
            },
            {
                valueType: 'dependency',
                name: [id, 'express_company'],
                columns: (value) => {
                    console.log(value);
                    const express_company = value[id]?.express_company;
                    return express_company === 'sfgj'
                        ? [
                              {
                                  valueType: 'select',
                                  dataIndex: [id, 'card_type'],
                                  title: intl.get('证件类型'),
                                  request: async () => {
                                      try {
                                          const res = await getCommonEnumInfo(
                                              'cardTypeEnum',
                                          );
                                          return res?.cardTypeEnum || [];
                                      } catch (e) {
                                          console.error(e);
                                          return [];
                                      }
                                  },
                                  formItemProps: {
                                      rules: isRequiredCard
                                          ? [
                                                {
                                                    required: true,
                                                    message:
                                                        intl.get(
                                                            '请选择证件类型',
                                                        ),
                                                },
                                            ]
                                          : [],
                                      labelCol: labelCol || { span: 3 },
                                  },
                              },
                              {
                                  valueType: 'text',
                                  title: intl.get('证件编号'),
                                  dataIndex: [id, 'id_card'],
                                  formItemProps: {
                                      rules: isRequiredCard
                                          ? [
                                                {
                                                    required: true,
                                                    message:
                                                        intl.get(
                                                            '请输入证件编号',
                                                        ),
                                                },
                                            ]
                                          : [],
                                      labelCol: labelCol || { span: 3 },
                                  },
                                  fieldProps: {
                                      maxLength: 18,
                                  },
                              },
                          ]
                        : [];
                },
            },
        ].filter((item) => !!item);

        return items;
    };

    const getOrderDetail = () => {
        if (address_info) {
            getTextByEncryp({
                type: position || 0,
                sourceNo,
                encryptList: [
                    address_info.phoneEncrypt,
                    address_info.addressEncrypt,
                ],
            }).then((res) => {
                if (res?.data?.length) {
                    onItemChange({
                        ...(address_info || {}),
                        phone: res?.data?.[0] || address_info.phone,
                        detail_address: res?.data?.[1] || address_info.address,
                    });
                } else {
                    message.warning(intl.get('无法获取解密后的信息'));
                    onItemChange({
                        ...(address_info || {}),
                        detail_address: address_info.address,
                    });
                }
            });
        } else {
            message.warning(intl.get('原订单无地址信息'));
        }
    };

    return (
        <div id={id}>
            {!readonly && showGetOriginBtn && (
                <Row>
                    <Col offset={labelCol?.span || 3}>
                        <Button
                            type="link"
                            size="small"
                            onClick={getOrderDetail}
                        >
                            {intl.get('原订单信息')}
                        </Button>
                    </Col>
                </Row>
            )}

            {readonly && (
                <div>
                    <BetaSchemaForm<any, 'addrSelectDate'>
                        formRef={addrForm}
                        key={type}
                        readonly={readonly}
                        layout="horizontal"
                        colon={true}
                        grid={true}
                        submitter={{
                            render: () => [],
                        }}
                        columns={getAddrColumns()}
                    ></BetaSchemaForm>
                </div>
            )}

            {!readonly && (
                <BetaSchemaForm<any, 'addrSelectDate'>
                    key={type}
                    readonly={readonly}
                    layout="horizontal"
                    layoutType="Embed"
                    columns={getAddrColumns()}
                ></BetaSchemaForm>
            )}
        </div>
    );
};

export default Addrs;
